import Logger from './Logger';
import { attachMessageListener, getDeviceInfo, getLocationQueryParam, setToken, checkExtension } from './Upshow';
import axios from 'axios';
import { delay } from 'bluebird';
import StateService from './services/StateService';

export function getWebviewName () {
    const url = new URL(window.location.href);

    //Hash is the highest priority webview name
    if (url.hash) {
        return { name: url.hash.replace('#', '').toLowerCase(), type: 'hash' };
    }

    //Second is ?name parameter
    if (url.searchParams.has('name')) {
        return { name: url.searchParams.get('name'), type: 'params' };
    }

    //Finally, if we didn't find a webview name, we'll use the subdomain if present, else we return undefined
    const match = new RegExp(/([\w-+]+)\.(localhost|upshowapp\.com|ui6\.internal\.upshow\.tv|ui6\.upshow\.tv)$/).exec(url.hostname);
    if (match) {
        return { name: match[1], type: 'subdomain' };
    }

    return null;
}

function getAccessToken () {
    // I got a Token in the query string
    const queryParamAccessToken = getLocationQueryParam('api_token');
    if (queryParamAccessToken) return queryParamAccessToken;

    // I have a Token saved in Local Storage
    const localStorageAccessToken = window.localStorage.getItem('access-token');
    if (localStorageAccessToken) return localStorageAccessToken;

    return null;
}

async function activationHelper (path) {
    let headers = {};

    const device_info = getDeviceInfo();
    if (device_info.serial) headers['x-serial'] = device_info.serial;

    return axios.get(process.env.REACT_APP_ACTIVATION_URL + path, { headers })
        .then(results => results.data ?? {})
        .catch(error => {
            Logger.error(['activation'], error, { path });
            throw error;
        });
}

export function cleanupStoredKeys () {
    window.localStorage.removeItem('access-token');
    window.localStorage.removeItem('activation-code');
}

class Activation {
    webviewName = null;
    accessToken = null;
    activationCode = null;

    constructor () {
        this.start = this.start.bind(this);
        this.cleanup = this.cleanup.bind(this);
        this.askForActivation = this.askForActivation.bind(this);
        this.autoActivate = this.autoActivate.bind(this);
        this.activation = this.activation.bind(this);
    }

    async waitForChromeOSExtension (DeviceUptime) {
        let first = 'Device is initializing... please standby.';
        let second = '';

        if (DeviceUptime > 10000) {
            first = 'Device encountered an issue during initialization. Please restart.';
            second = 'If issues persist, check for pending updates or contact support.';
        }

        let div = document.createElement('div');
        div.innerHTML = `<div class="logo">
                    <img alt="logo" src='https://cdn.upshow.tv/ui4_assets/upshow_logo.png'/>
                </div>
                <div class="first">${first}</div>
                <div class="second">${second}</div>`;
        div.id = 'waiting';
        document.body.insertBefore(div, document.getElementById('root'));
        await delay(3000);
        checkExtension();
        return this.start();
    }

    async start () {
        document.getElementById('waiting')?.remove();

        // Do I have a webviewname on the url?
        this.webviewName = getWebviewName();
        if (this.webviewName) {
            return this.autoActivate();
        }

        // Maybe I'm already activated
        this.accessToken = getAccessToken();
        if (this.accessToken) {
            setToken(this.accessToken);

            return this.accessToken;
        }

        // If we are in a PWA and the extension is not loaded yet, we wait for it
        const { extensionAvailable, model, DeviceUptime } = getDeviceInfo();
        if (model === 'ChromeOS PWA') {
            if (!extensionAvailable) {
                return this.waitForChromeOSExtension(DeviceUptime);
            }
        }

        return this.askForActivation();
    }

    cleanup () {
        this.accessToken = null;
        cleanupStoredKeys();
    }

    async askForActivation () {
        let code = localStorage.getItem('activation-code');

        if (!code) {
            const activationCodeResult = await activationHelper('/code')
                .catch(() => {
                    return {};
                });

            code = activationCodeResult.code;
            if (!code) {
                await delay(10000); // Add some delay before trying again
                return this.start();
            } else {
                localStorage.setItem('activation-code', code);
            }
        }

        let div = document.createElement('div');
        div.innerHTML = `<div class="instructions">This is your activation code</div>
                <div class="code">${code}</div>
                <div class="contact">
                    <div>If you need support</div>
                    <div>Email <strong>support@upshow.tv</strong></div>
                    <div>or text <strong>312.500.9200</strong></div>
                </div>
                <div class="logo">
                    <img alt="logo" src='https://cdn.upshow.tv/ui4_assets/upshow_logo.png'/>
                </div>`;
        div.className = 'Activation';
        document.body.insertBefore(div, document.getElementById('root'));
        this.activationCode = code;
        attachMessageListener();

        const api_key = await this.activation();

        document.body.removeChild(div);

        return api_key;
    }

    async activation () {
        let nextActivationAttempt = 2000;
        let keepTrying = true;

        while (keepTrying) {
            // If for some reason an access-token appears (extension probably)
            const localApiKey = window.localStorage.getItem('access-token');
            if (localApiKey) {
                this.accessToken = localApiKey;
                return localApiKey;
            }

            const activationResult = await activationHelper('/' + this.activationCode + '/activate')
                .then((result) => {
                    nextActivationAttempt = 2000;
                    return result;
                })
                .catch((error) => {
                    if (error.response.status === 432) {
                        StateService.showInactiveState();
                        StateService.stop();
                        return { inactive: true };
                    }

                    nextActivationAttempt = nextActivationAttempt * 2;
                    if (nextActivationAttempt > 60000) throw new Error('Activation is failing');
                    return {};
                });

            if (activationResult.inactive === true) {
                keepTrying = false;
            } else if (activationResult.active === true) {
                window.localStorage.setItem('access-token', activationResult.token);
                this.accessToken = activationResult.token;

                return activationResult.token;
            } else if (activationResult.expired === true) {
                this.cleanup();
                document.location.reload();
                keepTrying = false;
            }

            await delay(nextActivationAttempt); //Try again after this delay
        }
    }

    async autoActivate () {
        let path = `/webview/${encodeURI(this.webviewName.name)}/activate`;
        if (this.webviewName.type === 'subdomain') {
            path = path + '?type=pwa';
        }

        const activationResult = await activationHelper(path)
            .catch(error => {
                if (error.response.status === 432) {
                    StateService.showInactiveState();
                    StateService.stop();
                    return { inactive: true };
                } else if (error.response.status === 404) {
                    let url = new URL(window.location);
                    let params = url.searchParams;
                    let origin = url.origin;

                    switch (this.webviewName.type) {
                        case 'hash':
                            break;
                        case 'params':
                            params.delete('name');
                            break;
                        case 'subdomain':
                            origin = url.origin.replace(new RegExp('/' + this.webviewName.name + '\.'), '/');
                            break;
                        default:
                            throw new Error(`Invalid webviewName type ${this.webviewName.type}`);
                    }

                    window.location = origin + '?' + params.toString();

                    return {};
                }
                return {};
            });

        if (activationResult.inactive) {
            return;
        } else if (activationResult.active) {
            this.accessToken = activationResult.token;
            window.localStorage.setItem('access-token', this.accessToken);
            return this.accessToken;
        } else {
            await delay(1000);
            return this.start();
        }
    }
}

export default Activation;
